import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import debounce from 'lodash/debounce';
import { createSupabaseWithAuth } from '../Utils/SupabaseConfig';
import usePopupClose from '../Utils/usePopupClose';
import toast from 'react-hot-toast';

const AddCandidatePopup = ({ isOpen, onClose, onSuccess, jobId }) => {
  const [formData, setFormData] = useState({
    talentName: '',
    firstName: '',
    lastName: '',
    email: '',
    evaluationId: '',
    sendInviteEmail: false,
    existingTalentId: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [evaluations, setEvaluations] = useState([]);
  const [loadingEvaluations, setLoadingEvaluations] = useState(false);
  const [evaluationError, setEvaluationError] = useState(null);
  const [showNewTalentForm, setShowNewTalentForm] = useState(false);
  const [talentSearchResults, setTalentSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Get user session for authentication
  const { user } = useSession();
  
  // Setup popup close functionality
  const modalRef = usePopupClose(isOpen, onClose);

  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        talentName: '',
        firstName: '',
        lastName: '',
        email: '',
        evaluationId: '',
        sendInviteEmail: false,
        existingTalentId: null
      });
      setErrors({});
      setIsSubmitting(false);
      setShowNewTalentForm(false);
      setTalentSearchResults([]);
    }
  }, [isOpen]);
  
  // Fetch evaluations when popup is opened and jobId is available
  useEffect(() => {
    if (isOpen && jobId) {
      fetchEvaluations();
    }
  }, [isOpen, jobId]);
  
  // Function to fetch evaluations from the API
  const fetchEvaluations = async () => {
    setLoadingEvaluations(true);
    setEvaluationError(null);
    
    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make GET request to the evaluations endpoint with job ID as a parameter
      const url = new URL(API_ENDPOINTS.GET_JOB_EVALUATIONS);
      url.searchParams.append('jid', jobId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data
      const data = await response.json();
      console.log('Evaluations data:', data);
      
      // Set evaluations state
      setEvaluations(Array.isArray(data) ? data : []);
      
      // If there are evaluations, set the first one as default
      if (Array.isArray(data) && data.length > 0) {
        setFormData(prev => ({
          ...prev,
          evaluationId: data[0].evaluation_id
        }));
      }
    } catch (err) {
      console.error('Error fetching evaluations:', err);
      setEvaluationError('Failed to load evaluations. Please try again.');
    } finally {
      setLoadingEvaluations(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Function to fetch talent suggestions from Supabase
  const fetchTalentSuggestions = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setTalentSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      // Get the user's access token for authorization
      if (!user || !user.access_token) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Create a Supabase client with the authorization token
      const supabaseWithAuth = createSupabaseWithAuth(user.access_token);
      
      // Make the search query - filter by user ID and search term
      const { data, error } = await supabaseWithAuth
        .from('talent')
        .select('talent_id, fname, lname, email')
        .eq('uid', user.id) // Filter by user ID
        .or(`fname.ilike.%${searchTerm}%,lname.ilike.%${searchTerm}%`)
        .limit(10);
      
      if (error) {
        console.error('Talent search error:', error);
        return;
      }
      
      if (!data || data.length === 0) {
        setTalentSearchResults([]);
        return;
      }
      
      // Format the results for display
      const formattedResults = data.map(item => ({
        id: item.talent_id,
        name: `${item.fname} ${item.lname}`,
        email: item.email
      }));
      
      setTalentSearchResults(formattedResults);
    } catch (error) {
      console.error('Error searching for talent:', error);
      setTalentSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Create a debounced version of the search function
  const debouncedFetchTalent = useMemo(
    () => debounce(fetchTalentSuggestions, 300),
    []
  );
  
  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchTalent.cancel();
    };
  }, [debouncedFetchTalent]);
  
  // Function to handle talent search input changes
  const handleTalentSearch = (searchTerm) => {
    debouncedFetchTalent(searchTerm);
  };

  // Function to handle talent selection from search results
  const handleTalentSelect = (talent) => {
    setFormData({
      ...formData,
      talentName: talent.name,
      existingTalentId: talent.id,
      email: talent.email // Pre-fill email from selected talent
    });
    setTalentSearchResults([]);
  };

  // Function to toggle new talent form
  const toggleNewTalentForm = () => {
    setShowNewTalentForm(true);
    setFormData({
      ...formData,
      talentName: '',
      existingTalentId: null
    });
    setTalentSearchResults([]);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (showNewTalentForm) {
      // Validate first name (required)
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      
      // Validate last name (required)
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      
      // Validate email (required and format)
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
        newErrors.email = 'Invalid email address';
      }
    } else {
      // Validate talent selection
      if (!formData.existingTalentId && !formData.talentName.trim()) {
        newErrors.talentName = 'Please select or add a talent';
      }
    }
    
    // Validate evaluation selection
    if (!formData.evaluationId) {
      newErrors.evaluationId = 'Please select an evaluation';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to add a candidate (either with new talent or existing talent)
  const addCandidate = async () => {
    // Check if user exists
    if (!user) {
      throw new Error('User session not available. Please log in again.');
    }
    
    // Determine if we're using an existing talent or adding a new one
    const isExistingTalent = !showNewTalentForm && formData.existingTalentId;
    
    // Prepare the appropriate payload based on whether we're using an existing talent
    let payload;
    
    if (isExistingTalent) {
      // Payload for existing talent
      payload = {
        talent_id: formData.existingTalentId,
        job_id: jobId,
        evaluation_id: formData.evaluationId,
        send_invite_email: formData.sendInviteEmail
      };
      
      console.log('Adding candidate with existing talent:', payload);
    } else {
      // Payload for new talent
      payload = {
        fname: formData.firstName,
        lname: formData.lastName,
        job_id: jobId,
        email: formData.email,
        evaluation_id: formData.evaluationId,
        send_invite_email: formData.sendInviteEmail
      };
      
      console.log('Adding new candidate with new talent:', payload);
    }
    
    // Send the request to the same endpoint for both cases
    const response = await fetch(API_ENDPOINTS.ADD_CANDIDATE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      // Try to parse the error response body to get the server error message
      let errorMessage = `Error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError);
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }
    
    return await response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        // Add the candidate (either with new talent or existing talent)
        const result = await addCandidate();
        
        // Show success toast notification with server message
        const successMessage = result?.message || 'Candidate added successfully!';
        toast.success(successMessage);
        
        // Call the onSuccess callback if provided
        if (typeof onSuccess === 'function') {
          onSuccess(result);
        }
        
        onClose(); // Close popup on success
      } catch (error) {
        console.error('Error submitting candidate:', error);
        // Show error toast notification
        toast.error(error.message || 'Failed to invite candidate. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Add Candidate</h2>
          <p className="text-gray-600 mt-1">Add a candidate to this position</p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Talent Section */}
          <div className="pb-4">
            <h3 className="text-lg font-medium text-gray-800 mb-3">Talent</h3>
            
            {!showNewTalentForm ? (
              <>
                {/* Talent Search */}
                <div className="mb-3">
                  <label htmlFor="talentName" className="block text-sm font-medium text-gray-700 mb-1">
                    Talent Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    {formData.existingTalentId ? (
                      <div className="flex items-center">
                        <div className={`block w-full rounded-md sm:text-sm p-2 border border-gray-300 bg-gray-50 text-gray-700`}>
                          {formData.talentName}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              talentName: '',
                              existingTalentId: null,
                              email: ''
                            });
                          }}
                          className="absolute right-2 text-gray-500 hover:text-gray-700"
                          aria-label="Clear selection"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <input
                        type="text"
                        id="talentName"
                        name="talentName"
                        value={formData.talentName}
                        onChange={(e) => {
                          handleChange(e);
                          handleTalentSearch(e.target.value);
                        }}
                        className={`block w-full rounded-md sm:text-sm p-2 border ${
                          errors.talentName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                        placeholder="Start typing to search for talent..."
                        autoComplete="off"
                      />
                    )}
                    {isSearching && (
                      <div className="absolute right-3 top-2">
                        <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                    
                    {/* Search Results Dropdown */}
                    {talentSearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-300 max-h-60 overflow-auto">
                        {talentSearchResults.map((talent) => (
                          <div 
                            key={talent.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleTalentSelect(talent)}
                          >
                            <div className="font-medium">{talent.name}</div>
                            <div className="text-sm text-gray-600">{talent.email}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.talentName && (
                    <p className="mt-1 text-sm text-red-600">{errors.talentName}</p>
                  )}
                </div>
                
                {/* Or Add New Talent Button */}
                <button
                  type="button"
                  onClick={toggleNewTalentForm}
                  className="w-full py-2 px-4 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  OR - Create New Talent
                </button>
              </>
            ) : (
              <>
                {/* New Talent Form */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  {/* First Name */}
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`block w-full rounded-md sm:text-sm p-2 border ${
                        errors.firstName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      placeholder="Enter first name"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                    )}
                  </div>
                  
                  {/* Last Name */}
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`block w-full rounded-md sm:text-sm p-2 border ${
                        errors.lastName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                      }`}
                      placeholder="Enter last name"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                    )}
                  </div>
                </div>
                
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full rounded-md sm:text-sm p-2 border ${
                      errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
                
                {/* Back to Search Button */}
                <button
                  type="button"
                  onClick={() => setShowNewTalentForm(false)}
                  className="mt-3 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  ← Back to talent search
                </button>
              </>
            )}
          </div>
          
          {/* Divider */}
          <div className="border-t border-gray-200 my-4"></div>
          
          {/* Evaluation Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Evaluation</h3>
            
            {/* Evaluation Selection */}
            <div>
              <label htmlFor="evaluationId" className="block text-sm font-medium text-gray-700 mb-1">
                Select Evaluation <span className="text-red-500">*</span>
              </label>
              {loadingEvaluations ? (
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Loading evaluations...</span>
                </div>
              ) : evaluationError ? (
                <div className="text-sm text-red-600 mb-2">{evaluationError}</div>
              ) : evaluations.length === 0 ? (
                <div className="text-sm text-amber-600 mb-2">No evaluations available for this job. Please create an evaluation first.</div>
              ) : (
                <select
                  id="evaluationId"
                  name="evaluationId"
                  value={formData.evaluationId}
                  onChange={handleChange}
                  className={`block w-full rounded-md sm:text-sm p-2 border ${
                    errors.evaluationId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                >
                  <option value="">Select an evaluation</option>
                  {evaluations.map((evaluation) => (
                    <option key={evaluation.evaluation_id} value={evaluation.evaluation_id}>
                      {evaluation.name || `Evaluation ${evaluation.evaluation_id}`}
                    </option>
                  ))}
                </select>
              )}
              {errors.evaluationId && (
                <p className="mt-1 text-sm text-red-600">{errors.evaluationId}</p>
              )}
            </div>
            
            {/* Send Invite Email Checkbox */}
            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                id="sendInviteEmail"
                name="sendInviteEmail"
                checked={formData.sendInviteEmail}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="sendInviteEmail" className="ml-2 block text-sm text-gray-700">
                Send invite email
              </label>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Inviting...
                </span>
              ) : (
                'Invite'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCandidatePopup;
